/**
 * Admin Role Badge Component
 *
 * Displays a super admin user's role with distinctive color coding.
 * Colors are designed for clear visual hierarchy and quick recognition.
 */
import { cn } from '@respectly/ui';
import type { SuperAdminRole } from '@respectly/shared-types';

// ============================================
// Role Configuration
// ============================================

const ROLE_CONFIG: Record<
  SuperAdminRole,
  {
    label: string;
    bgColor: string;
    textColor: string;
    borderColor: string;
    dotColor: string;
  }
> = {
  SuperAdmin: {
    label: 'Super Admin',
    bgColor: 'bg-violet-50 dark:bg-violet-950/40',
    textColor: 'text-violet-700 dark:text-violet-300',
    borderColor: 'border-violet-200 dark:border-violet-800/60',
    dotColor: 'bg-violet-500',
  },
  SupportAdmin: {
    label: 'Support',
    bgColor: 'bg-blue-50 dark:bg-blue-950/40',
    textColor: 'text-blue-700 dark:text-blue-300',
    borderColor: 'border-blue-200 dark:border-blue-800/60',
    dotColor: 'bg-blue-500',
  },
  BillingAdmin: {
    label: 'Billing',
    bgColor: 'bg-emerald-50 dark:bg-emerald-950/40',
    textColor: 'text-emerald-700 dark:text-emerald-300',
    borderColor: 'border-emerald-200 dark:border-emerald-800/60',
    dotColor: 'bg-emerald-500',
  },
  ComplianceAdmin: {
    label: 'Compliance',
    bgColor: 'bg-amber-50 dark:bg-amber-950/40',
    textColor: 'text-amber-700 dark:text-amber-300',
    borderColor: 'border-amber-200 dark:border-amber-800/60',
    dotColor: 'bg-amber-500',
  },
  ReadOnlyAdmin: {
    label: 'Read Only',
    bgColor: 'bg-slate-100 dark:bg-slate-800/60',
    textColor: 'text-slate-600 dark:text-slate-400',
    borderColor: 'border-slate-200 dark:border-slate-700',
    dotColor: 'bg-slate-400',
  },
};

// ============================================
// Size Configuration
// ============================================

const SIZE_STYLES = {
  sm: {
    container: 'px-2 py-0.5 text-xs gap-1.5',
    dot: 'w-1.5 h-1.5',
  },
  md: {
    container: 'px-2.5 py-1 text-sm gap-2',
    dot: 'w-2 h-2',
  },
};

// ============================================
// Component Props
// ============================================

export interface AdminRoleBadgeProps {
  role: SuperAdminRole;
  size?: 'sm' | 'md';
  showDot?: boolean;
  className?: string;
}

// ============================================
// Component
// ============================================

export function AdminRoleBadge({
  role,
  size = 'sm',
  showDot = true,
  className,
}: AdminRoleBadgeProps) {
  const config = ROLE_CONFIG[role];
  const sizeStyles = SIZE_STYLES[size];

  return (
    <span
      className={cn(
        'inline-flex items-center font-medium rounded-full border',
        'transition-colors duration-150',
        config.bgColor,
        config.textColor,
        config.borderColor,
        sizeStyles.container,
        className
      )}
    >
      {showDot && (
        <span
          className={cn(
            'rounded-full flex-shrink-0',
            config.dotColor,
            sizeStyles.dot
          )}
        />
      )}
      <span className="truncate">{config.label}</span>
    </span>
  );
}

// ============================================
// Utility Functions
// ============================================

/** Get the display label for a role */
export function getRoleLabel(role: SuperAdminRole): string {
  return ROLE_CONFIG[role].label;
}

/** Get the primary color class for a role */
export function getRoleColor(role: SuperAdminRole): string {
  return ROLE_CONFIG[role].dotColor;
}

/** Get all role options for select dropdowns */
export function getRoleOptions(): { value: SuperAdminRole; label: string }[] {
  return (Object.keys(ROLE_CONFIG) as SuperAdminRole[]).map((role) => ({
    value: role,
    label: ROLE_CONFIG[role].label,
  }));
}
